import { useEffect } from 'react';
import type { AccessState } from '@bbbk/schema';
import { ActionButton, ActionStack, Icon, MessagePage } from '../components/common';
import { ProviderButtons } from './ProviderButtons';
import { CUSTOMER_SIGNIN_PATH } from './joinOutcome';
import { MessagePatricia } from './MessagePatricia';
import { recordPawSeat } from './pawFlight';

// `c1b` — John's door. The screen a registration link opens, and the first
// customer-facing page this app has ever had.
//
// IT IS THE ONLY WAY IN (R-ACC-3). There is no self sign-up, no email link and
// no "get started": the desk issues every link, and this page's whole job is to
// turn one into a signed-in identity. Row 3 extends it with the registration
// steps that follow; it does not create it.
//
// WHAT IT SAYS WHEN IT CANNOT LET SOMEBODY IN is most of the code below, and
// deliberately so. A dead link is the state Patricia will see most (the window
// is 24 hours), so this page has to read as ordinary rather than as a fault,
// and it has to lead with the fix — which is always the same fix: ring her.

export type RefusalReason = 'account_in_use' | 'link_dead';

export interface JoinDoorProps {
  state: AccessState;
  contactName: string | null;
  /** Set when the callback bounced them back here rather than signing them in. */
  refused: RefusalReason | null;
  /** AC11 — who finished on this link first. */
  /** Whether this dead link offers a way on — see `offersSignIn`. */
  offerSignIn?: boolean;
  token: string;
}

/** What the page leads with when the link cannot be used. */
function deadCopy(state: AccessState, refused: RefusalReason | null) {
  if (refused === 'account_in_use') {
    return {
      title: 'That account is already in use',
      body:
        'The email you signed in with already has an account here, so we have not changed anything. ' +
        'Give Patricia a ring and she will sort it out.',
    };
  }
  if (refused === 'link_dead') {
    return {
      title: 'That link no longer works',
      body: 'It ran out or was replaced while you were signing in. Patricia can send you a fresh one.',
    };
  }
  if (state === 'part') {
    // WHO USED IT IS NOT THIS PAGE'S TO SAY (Freddi, 2026-09-16).
    //
    // It named the claimer, masked — a first letter, a domain and a length,
    // which identifies somebody perfectly well to anyone who knows them. A
    // link travels, and the one person guaranteed not to be entitled to that
    // is whoever it travelled to.
    //
    // The words lose nothing: both readers get the move that is theirs. The
    // person who used it recognises themselves in "if that was you" and signs
    // in; the person who did not is told, in the same breath, that a link of
    // their own is a message away. Naming the address only ever confirmed what
    // one of them already knew.
    return {
      title: 'This link has already been used',
      body:
        'Somebody has already registered with it. If that was you, sign in and you will land ' +
        'straight on your stays. If not, Patricia can send you your own.',
    };
  }
  if (state === 'expired' || state === 'revoked') {
    // ONE SCREEN FOR BOTH, and the same words (Freddi, 2026-09-14). Beyond
    // the tidiness there is a quiet reason: a page that said "no longer active"
    // told whoever held the link that the desk had cut them off. "Expired"
    // tells them nothing they can act on except the two things offered — and
    // sign-in refuses a revoked account anyway, which is the right outcome.
    return {
      title: 'This link has expired',
      body: 'Links last 24 hours. If you’ve already registered, sign in. If not, Patricia can send a fresh one.',
    };
  }
  return { title: 'This link is not one of ours', body: 'Check the message it came in, or ring Patricia.' };
}

export function JoinDoor({ state, contactName, refused, offerSignIn = false, token }: JoinDoorProps) {
  const firstName = contactName?.trim().split(/\s+/)[0] ?? null;

  // WHERE THIS PAGE'S PAW IS SITTING, written down for the wizard on the other
  // side of the provider (Freddi, 2026-09-16 — see `pawFlight`).
  //
  // Recorded on layout rather than on the tap: whichever provider they choose,
  // whether they tap at all, and whether they come back in ten seconds or ten
  // minutes, the last note is the true one. Coupling it to a button would mean
  // plumbing a ref through `ProviderButtons` for a value that is a property of
  // the page, not of the click.
  //
  // `querySelector` because the mark belongs to `MessagePage`, which draws it
  // through `MessageBrand` and exposes no ref. One page, one opening mark — and
  // if the selector ever misses, `recordPawSeat(null)` writes nothing and the
  // wizard uses its proportional fallback. Nothing downstream depends on this
  // succeeding.
  useEffect(() => {
    const read = () => recordPawSeat(document.querySelector('.mp-mark.lg'));
    read();
    // AND AGAIN ONCE THE PAGE HAS SETTLED. The first read happens before the
    // webfont has swapped in, and the name under the mark changing metrics
    // moves the mark — a note taken a frame too early is a note that is wrong.
    const raf = requestAnimationFrame(read);
    window.addEventListener('resize', read);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', read);
    };
  }, []);
  // USABLE MEANS ONE STATE HERE. An expired or already-used link still reaches
  // this component, but as a page that explains itself and offers the way on
  // (flow 1.6, and Freddi 2026-09-12). What is left is the refusals, a revoked
  // link, and a token nobody issued.
  const canSignIn = refused === null && state === 'invited';
  const dead = canSignIn ? null : deadCopy(state, refused);

  if (dead) {
    return (
      <MessagePage
        title={dead.title}
        lead={dead.body}
        actions={
          // The way on first, Patricia quiet beneath it — the shape every
          // dead-end page a phone user has seen already takes.
          <ActionStack>
            {offerSignIn && (
              <ActionButton tone="primary" href={CUSTOMER_SIGNIN_PATH} icon={<Icon name="log-in" size={15} />}>
                Sign in
              </ActionButton>
            )}
            <MessagePatricia />
          </ActionStack>
        }
      />
    );
  }

  return (
    <MessagePage
      title={firstName ? `Patricia has added you, ${firstName}` : 'Patricia has added you'}
      lead="Sign in to see your stays, book the dogs in, and pay — the same as everyone else on your household."
      actions={<ProviderButtons />}
      foot={
        <>
          Apple sign-in is coming — use Google or Microsoft for now. This link expires in 24 hours; if it
          runs out, Patricia will send another.
          {/* The token is on screen nowhere and in the address bar always: it is
              how they got here, and repeating it would only invite a screenshot. */}
          <span hidden data-token={token} />
        </>
      }
    />
  );
}
